import type { StateCreator } from "../store";
import type { AppStore } from "../index";
import { Community } from "@/data/schemas";
import {
  getPlatformStorageItem,
  setPlatformStorageItem,
} from "@/data/storage/platform_storage";

export interface CommunitySlice {
  // State
  communityMap: Record<string, Community>;
  selectedCommunityCursor: string | null;

  // Actions
  setCommunity: (community: Community) => void;
  setCommunities: (communities: Community[]) => void;
  updateCommunity: (communityId: string, updates: Partial<Community>) => void;
  removeCommunity: (communityId: string) => void;
  clearCommunities: () => void;
  setSelectedCommunityCursor: (cursor: string | null) => void;

  // Getters (computed values)
  getCommunityList: () => Community[];
  getSelectedCommunity: () => Community | null;
  getCommunity: (communityId: string) => Community | undefined;
}

// Helper to load communities from localStorage
const loadCommunitiesFromStorage = (): Record<string, Community> => {
  try {
    const stored = getPlatformStorageItem("switch-communities");
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('[CommunitySlice] Failed to load from localStorage:', error);
    return {};
  }
};

// Helper to save communities to localStorage
const saveCommunitiesToStorage = (communityMap: Record<string, Community>) => {
  try {
    setPlatformStorageItem("switch-communities", JSON.stringify(communityMap));
  } catch (error) {
    console.error('[CommunitySlice] Failed to save to localStorage:', error);
  }
};

export const createCommunitySlice: StateCreator<AppStore, unknown, unknown, CommunitySlice> = (set, get) => ({
  // Initial state - load from localStorage
  communityMap: loadCommunitiesFromStorage(),
  selectedCommunityCursor: null,

  // Actions
  setCommunity: (community) =>
    set((state) => {
      // Support both `id` and `communityId` keys for flexibility
      const key = (community as any).communityId || (community as any).id;
      if (key) {
        state.communityMap[key] = community;
        // Persist to localStorage
        saveCommunitiesToStorage(state.communityMap);
      }
    }),

  setCommunities: (communities) =>
    set((state) => {
      communities.forEach((c) => {
        const key = (c as any).communityId || (c as any).id;
        if (key) {
          state.communityMap[key] = c;
        }
      });
      // Persist to localStorage
      saveCommunitiesToStorage(state.communityMap);
    }),

  updateCommunity: (communityId, updates) =>
    set((state) => {
      if (state.communityMap[communityId]) {
        Object.assign(state.communityMap[communityId], updates);
        // Persist to localStorage
        saveCommunitiesToStorage(state.communityMap);
      }
    }),

  removeCommunity: (communityId) =>
    set((state) => {
      delete state.communityMap[communityId];
      // Persist to localStorage
      saveCommunitiesToStorage(state.communityMap);
    }),

  clearCommunities: () =>
    set((state) => {
      state.communityMap = {};
      // Persist to localStorage
      saveCommunitiesToStorage(state.communityMap);
    }),

  setSelectedCommunityCursor: (cursor) =>
    set((state) => {
      state.selectedCommunityCursor = cursor;
    }),

  // Getters
  getCommunityList: () => {
    return Object.values(get().communityMap).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  },

  getSelectedCommunity: () => {
    const { selectedCommunityCursor, communityMap } = get();
    if (selectedCommunityCursor) {
      return communityMap[selectedCommunityCursor] || null;
    }
    return null;
  },

  getCommunity: (communityId) => get().communityMap[communityId],
});
